import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Scale, Plus, Trash2, Pencil as PencilIcon, Check, X as XIcon, GitBranch,
  Trophy, Crown, AlertCircle, Sparkles, ChevronRight, ChevronDown, ArrowUpRight,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { PageHeader } from "@/components/common/PageHeader";
import { GlassCard } from "@/components/common/GlassCard";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Phase 13B — Decision Matrix (Layer 3 · Strategy)
// ---------------------------------------------------------------------------
// Weighted scoring for forks like "Sign with Agent A vs B", "Test or sit",
// "Take Visit X". Family role read-only; admin owns CRUD.

const STATUS_TONE = {
  open:     { tone: "neutral", label: "Open"      },
  leaning:  { tone: "amber",   label: "Leaning"   },
  decided:  { tone: "emerald", label: "Decided"   },
  archived: { tone: "neutral", label: "Archived"  },
};

function auditActionLabel(e) {
  if (e.action === "created")         return "Decision created";
  if (e.action === "status_change")   return `Status: ${e.from_status || "—"} → ${e.to_status || "—"}`;
  if (e.action === "chosen_option")   return e.to_option ? `Chose "${e.to_option}"` : "Cleared choice";
  if (e.action === "rationale_updated") return "Rationale updated";
  return e.action;
}

function emptyDecision() {
  return {
    title: "",
    question: "",
    summary: "",
    criteria: [
      { name: "Career fit", weight: 2 },
      { name: "Financial outcome", weight: 1.5 },
      { name: "Family alignment", weight: 1.5 },
      { name: "Risk", weight: 1 },
    ],
    options: [{ name: "Option A", summary: "", scores: {} }],
    chosen_option: null,
    decision_rationale_md: "",
    status: "open",
    visible_to_family: true,
  };
}

export default function DecisionsPage() {
  useDocumentTitle("OmniDraftLab · Decisions");
  const { user } = useAuth();
  const { playerId: paramPlayerId } = useParams();
  const isAdmin = user?.role === "admin";
  const playerId = paramPlayerId || user?.player_id;

  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    if (!playerId) return setDecisions([]);
    const r = await api.get("/decisions", { params: { player_id: playerId } });
    setDecisions(r.data || []);
  }, [playerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refresh().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refresh]);

  if (loading) return <LoadingState />;
  if (!playerId) return <EmptyState icon={Scale} title="No athlete linked" />;

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="decisions-page">
      <PageHeader
        title="Decision Matrix"
        subtitle="Every fork in the road, weighed on the same scale. Show your work."
        actions={isAdmin && (
          <Button data-testid="decisions-new" onClick={() => setEditing("new")} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold rounded-xl">
            <Plus className="h-4 w-4 mr-1" /> New decision
          </Button>
        )}
      />

      {decisions.length === 0 ? (
        <div className="mt-6">
          <EmptyState
            icon={GitBranch}
            title="No decisions logged yet"
            description={isAdmin ? "Add the first major fork — sign vs not, test vs sit, accept vs decline." : "Your advisor hasn't shared any decisions yet."}
          />
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {decisions.map((d, i) => (
            <DecisionCard
              key={d.id}
              decision={d}
              index={i}
              isAdmin={isAdmin}
              onEdit={() => setEditing(d)}
              onDelete={async () => {
                if (!confirm(`Delete "${d.title}"?`)) return;
                await api.delete(`/decisions/${d.id}`);
                toast.success("Decision removed");
                await refresh();
              }}
            />
          ))}
        </div>
      )}

      {editing && (
        <DecisionDialog
          open={!!editing}
          onClose={() => setEditing(null)}
          initial={editing === "new" ? emptyDecision() : editing}
          isNew={editing === "new"}
          playerId={playerId}
          onSaved={async (msg) => { toast.success(msg); setEditing(null); await refresh(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Decision card
// ---------------------------------------------------------------------------
function DecisionCard({ decision, index, isAdmin, onEdit, onDelete }) {
  const status = STATUS_TONE[decision.status] || STATUS_TONE.open;
  const [expanded, setExpanded] = useState(decision.status !== "archived");
  const ws = decision.weighted_scores || {};
  const winner = useMemo(() => {
    const entries = Object.entries(ws).filter(([, v]) => v != null);
    if (!entries.length) return null;
    return entries.sort((a, b) => b[1] - a[1])[0];
  }, [ws]);
  const maxScore = winner ? winner[1] : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`decision-card-${decision.id}`}
    >
      <GlassCard className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-3 text-[10px] uppercase tracking-wider">
              <GitBranch className="h-3 w-3" /> Decision · {decision.options?.length || 0} options
            </div>
            <h3 className="mt-1 text-1 text-xl font-semibold tracking-tight">{decision.title}</h3>
            {decision.question && <p className="mt-1 text-2 text-sm">{decision.question}</p>}
          </div>
          <div className="flex items-center gap-2">
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
            <button onClick={() => setExpanded(!expanded)} className="text-3 hover:text-1" data-testid={`decision-toggle-${decision.id}`}>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {decision.chosen_option && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-300/15 ring-1 ring-emerald-300/30 text-emerald-200 text-xs">
            <Crown className="h-3 w-3" /> Chosen · {decision.chosen_option}
          </div>
        )}

        {expanded && (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-3 text-[10px] uppercase tracking-wider">
                  <th className="text-left py-2 pr-3 font-medium sticky left-0 bg-[color:var(--canvas-2)]">Criterion</th>
                  <th className="text-right py-2 px-2 font-medium font-mono">Weight</th>
                  {(decision.options || []).map((o) => (
                    <th key={o.name} className="text-left py-2 px-3 font-medium min-w-[150px]">
                      <div className="text-1 text-sm font-semibold flex items-center gap-1.5">
                        {o.name}
                        {decision.chosen_option === o.name && <Crown className="h-3 w-3 text-emerald-300" />}
                      </div>
                      {o.summary && <div className="text-3 text-[10px] truncate normal-case font-normal tracking-normal">{o.summary}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(decision.criteria || []).map((c, ci) => (
                  <tr key={ci} className="border-t border-white/5">
                    <td className="py-2.5 pr-3 text-1 sticky left-0 bg-[color:var(--canvas-2)]">{c.name}</td>
                    <td className="py-2.5 px-2 text-right text-3 font-mono text-xs">×{c.weight}</td>
                    {(decision.options || []).map((o) => {
                      const v = (o.scores || {})[c.name];
                      return (
                        <td key={o.name} className={cn(
                          "py-2.5 px-3 font-mono",
                          v == null ? "text-3" : v >= 8 ? "text-emerald-300" : v >= 6 ? "text-cyan-300" : v >= 4 ? "text-amber-300" : "text-rose-300"
                        )}>
                          {v != null ? Number(v).toFixed(1) : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="border-t border-white/10 bg-white/[0.03]">
                  <td className="py-3 pr-3 text-1 font-semibold sticky left-0 bg-[color:var(--canvas-2)]" colSpan={2}>Weighted total</td>
                  {(decision.options || []).map((o) => {
                    const v = ws[o.name];
                    const isMax = v != null && v === maxScore && maxScore > 0;
                    return (
                      <td key={o.name} className={cn(
                        "py-3 px-3 font-mono text-base",
                        v == null ? "text-3" : isMax ? "text-emerald-300 font-semibold" : "text-1"
                      )}>
                        {v != null ? v.toFixed(1) : "—"}{isMax && " ★"}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>

            {decision.decision_rationale_md && (
              <div className="mt-4 p-4 glass">
                <div className="text-3 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3 text-cyan-300" /> Rationale
                </div>
                <div className="markdown-body text-2 text-sm prose prose-invert prose-headings:text-1 max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{decision.decision_rationale_md}</ReactMarkdown>
                </div>
              </div>
            )}

            {(decision.audit_log || []).length > 0 && (
              <div className="mt-4 p-4 glass" data-testid={`decision-audit-${decision.id}`}>
                <div className="text-3 text-[10px] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-cyan-300" /> Audit log · {decision.audit_log.length} {decision.audit_log.length === 1 ? "entry" : "entries"}
                </div>
                <ol className="space-y-2 relative border-l border-white/10 pl-4">
                  {[...decision.audit_log].reverse().map((e, i) => (
                    <li key={i} className="text-2 text-xs relative">
                      <span className="absolute -left-[21px] top-1 h-2 w-2 rounded-full bg-cyan-300 ring-2 ring-[#04111A]" />
                      <div className="flex flex-wrap items-baseline gap-x-2">
                        <span className="text-1 font-medium">{auditActionLabel(e)}</span>
                        <span className="text-3 text-[10px] font-mono">{new Date(e.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                      </div>
                      <div className="text-3 text-[11px] mt-0.5">by {e.by || "—"}</div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {isAdmin && (
          <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-end gap-1">
            <Button data-testid={`decision-edit-${decision.id}`} onClick={onEdit} variant="ghost" size="sm" className="text-2 hover:text-white text-xs"><PencilIcon className="h-3 w-3 mr-1" /> Edit</Button>
            <Button data-testid={`decision-delete-${decision.id}`} onClick={onDelete} variant="ghost" size="sm" className="text-2 hover:text-rose-300 text-xs"><Trash2 className="h-3 w-3 mr-1" /> Delete</Button>
          </div>
        )}
      </GlassCard>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit dialog
// ---------------------------------------------------------------------------
function DecisionDialog({ open, onClose, initial, isNew, playerId, onSaved }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(initial); }, [initial]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const updateCriterion = (i, patch) => {
    const c = [...form.criteria];
    c[i] = { ...c[i], ...patch };
    set({ criteria: c });
  };
  const addCriterion = () => set({ criteria: [...form.criteria, { name: "New criterion", weight: 1 }] });
  const removeCriterion = (i) => {
    const removedName = form.criteria[i].name;
    const c = form.criteria.filter((_, idx) => idx !== i);
    const o = form.options.map((opt) => {
      const { [removedName]: _x, ...rest } = (opt.scores || {});
      return { ...opt, scores: rest };
    });
    set({ criteria: c, options: o });
  };

  const updateOption = (i, patch) => {
    const o = [...form.options];
    o[i] = { ...o[i], ...patch };
    set({ options: o });
  };
  const addOption = () => set({ options: [...form.options, { name: `Option ${String.fromCharCode(65 + form.options.length)}`, summary: "", scores: {} }] });
  const removeOption = (i) => set({ options: form.options.filter((_, idx) => idx !== i) });
  const setScore = (oi, criterionName, value) => {
    const o = [...form.options];
    const scores = { ...(o[oi].scores || {}) };
    if (value === "" || value == null) delete scores[criterionName];
    else scores[criterionName] = Number(value);
    o[oi] = { ...o[oi], scores };
    set({ options: o });
  };

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title required");
    if (!(form.criteria?.length)) return toast.error("Add at least 1 criterion");
    if (!(form.options?.length)) return toast.error("Add at least 1 option");
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/decisions", { ...form, player_id: playerId });
        await onSaved("Decision created");
      } else {
        await api.patch(`/decisions/${form.id}`, form);
        await onSaved("Decision updated");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass-strong border-white/10 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="decision-dialog">
        <DialogHeader>
          <DialogTitle className="text-1">{isNew ? "New decision" : "Edit decision"}</DialogTitle>
          <DialogDescription className="text-3 text-xs">Frame the question, list options, weight criteria, score 1-10. The matrix will reveal the answer.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-12 gap-3 mt-2">
          <div className="col-span-12 md:col-span-9 space-y-1.5">
            <Label className="text-2 text-xs">Title *</Label>
            <Input data-testid="decision-form-title" value={form.title || ""} onChange={(e) => set({ title: e.target.value })} className="bg-white/5 border-white/10" />
          </div>
          <div className="col-span-12 md:col-span-3 space-y-1.5">
            <Label className="text-2 text-xs">Status</Label>
            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger className="bg-white/5 border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10">
                {Object.entries(STATUS_TONE).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-12 space-y-1.5">
            <Label className="text-2 text-xs">Question</Label>
            <Input value={form.question || ""} onChange={(e) => set({ question: e.target.value })} className="bg-white/5 border-white/10" placeholder="e.g. Which agent should we sign with?" />
          </div>

          {/* Criteria */}
          <div className="col-span-12">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-1 text-sm font-semibold">Criteria</Label>
              <Button variant="ghost" size="sm" onClick={addCriterion} className="text-cyan-300 hover:text-cyan-200 text-xs"><Plus className="h-3 w-3 mr-1" /> Add criterion</Button>
            </div>
            <div className="space-y-2">
              {form.criteria.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={c.name} onChange={(e) => updateCriterion(i, { name: e.target.value })} className="flex-1 bg-white/5 border-white/10" />
                  <div className="w-32 flex items-center gap-2">
                    <Label className="text-3 text-[11px]">×</Label>
                    <Input type="number" step="0.5" value={c.weight} onChange={(e) => updateCriterion(i, { weight: Number(e.target.value) })} className="bg-white/5 border-white/10" />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeCriterion(i)} className="text-2 hover:text-rose-300"><XIcon className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="col-span-12">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-1 text-sm font-semibold">Options</Label>
              <Button variant="ghost" size="sm" onClick={addOption} className="text-cyan-300 hover:text-cyan-200 text-xs"><Plus className="h-3 w-3 mr-1" /> Add option</Button>
            </div>
            <div className="space-y-3">
              {form.options.map((o, oi) => (
                <div key={oi} className="glass p-3">
                  <div className="flex items-center gap-2">
                    <Input value={o.name} onChange={(e) => updateOption(oi, { name: e.target.value })} className="flex-1 bg-white/5 border-white/10 font-semibold" />
                    <Button variant="ghost" size="sm" onClick={() => set({ chosen_option: o.name })} className={cn("text-xs", form.chosen_option === o.name ? "text-emerald-300" : "text-2 hover:text-emerald-300")}>
                      <Crown className="h-3 w-3 mr-1" /> {form.chosen_option === o.name ? "Chosen" : "Choose"}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeOption(oi)} className="text-2 hover:text-rose-300"><XIcon className="h-3.5 w-3.5" /></Button>
                  </div>
                  <Input value={o.summary || ""} onChange={(e) => updateOption(oi, { summary: e.target.value })} className="mt-2 bg-white/5 border-white/10 text-sm" placeholder="One-line description (optional)" />
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                    {form.criteria.map((c) => (
                      <div key={c.name} className="flex items-center gap-2">
                        <Label className="flex-1 text-2 text-xs truncate">{c.name}</Label>
                        <Input type="number" step="0.5" min="1" max="10" value={o.scores?.[c.name] ?? ""} onChange={(e) => setScore(oi, c.name, e.target.value)} className="w-20 bg-white/5 border-white/10 text-sm" placeholder="1-10" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 space-y-1.5">
            <Label className="text-2 text-xs">Rationale (markdown)</Label>
            <Textarea rows={4} value={form.decision_rationale_md || ""} onChange={(e) => set({ decision_rationale_md: e.target.value })} className="bg-white/5 border-white/10 text-1" placeholder="Why we landed where we did. Document for the family." />
          </div>
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button variant="ghost" onClick={onClose} className="text-2">Cancel</Button>
          <Button data-testid="decision-save" onClick={save} disabled={busy} className="bg-cyan-300 text-[#04111A] hover:bg-cyan-200 font-semibold">
            <Check className="h-4 w-4 mr-1" /> {isNew ? "Create" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
